<!--
 * @Description: 子客户代码
 * @Author: cxh
 * @Date: 2022-05-24 15:19:34
 * @LastEditTime: 2023-03-10 10:57:46
 * @LastEditors: cxh
-->
<template>
  <dc-layout-main :is-breadcrumb="true">
    <dc-layout-content>
      <cos-table
        serial
        :columns="columns"
        v-model:search="searchFrom"
        @fetch="fetch"
        @mask="showMask"
        ref="tableRef"
        tableId="ofbuAccountCode"
      >
        <template #search>
          <a-form :model="searchFrom">
            <dc-form-item :label="t('arkCode.customerNumber')">
              <a-input v-model:value="searchFrom.customerNumber" :placeholder="t('common.pleaseInput')" />
            </dc-form-item>
            <dc-form-item :label="t('arkCode.customerName')">
              <a-input v-model:value="searchFrom.customerName" allowClear :placeholder="t('common.pleaseInput')" />
            </dc-form-item>
            <dc-form-item :label="t('arkCode.arkContactMobile')">
              <a-input v-model:value="searchFrom.arkContactMobile" :placeholder="t('common.pleaseInput')" />
            </dc-form-item>
            <dc-form-item name="stage" :label="t('arkCode.stage')">
              <CosSelect
                v-model:value="searchFrom.stage"
                :placeholder="t('common.pleaseSelect')"
                mode="multiple"
                allowClear
                :options="dataConfig.customerPhaseEnum"
              />
            </dc-form-item>
            <dc-form-item :label="t('arkCode.arkCode')">
              <a-input v-model:value="searchFrom.arkCode" allowClear :placeholder="t('common.pleaseInput')" />
            </dc-form-item>
            <dc-form-item :label="t('arkCode.arkAccount')">
              <a-input v-model:value="searchFrom.arkAccount" allowClear :placeholder="t('common.pleaseInput')" />
            </dc-form-item>
            <dc-form-item name="level" :label="t('arkCode.level')">
              <a-select
                v-model:value="searchFrom.level"
                :fieldNames="{ label: 'configName', value: 'id' }"
                mode="multiple"
                allowClear
                :placeholder="t('common.pleaseSelect')"
                :options="level"
              />
            </dc-form-item>
            <dc-form-item name="seller" :label="t('arkCode.sellerName')">
              <user-select v-model:value="searchFrom.seller" />
            </dc-form-item>
            <dc-form-item name="createTime" :label="t('common.createTime')">
              <CosRangePicker
                isOutputBeijingTime
                v-model:startTime="searchFrom.createTimeStart"
                v-model:endTime="searchFrom.createTimeEnd"
              />
            </dc-form-item>
          </a-form>
        </template>
        <template #button>
          <a-button type="primary" @click="addEdit" v-permission="'ofbu_arkcode_add_btn'">{{ t('btn.add') }} </a-button>
          <ExportButton appId="cos_ofbu" taskType="406" :search="searchFrom" v-permission="'ofbu_arkcode_export_btn'">{{
            t('btn.export')
          }}</ExportButton>
        </template>
        <template #bodyCell="{ column, record, text }">
          <template v-if="column.dataIndex === 'action'">
            <MoreButton>
              <a-button type="link" @click="addEdit(record)" v-permission="'ofbu_arkcode_edit_btn'">
                {{ t('btn.edit') }}
              </a-button>
              <a-button type="link" @click="deleteRow(record)" v-permission="'ofbu_arkcode_del_btn'">
                {{ t('btn.del') }}
              </a-button>
            </MoreButton>
          </template>
          <template v-if="column.dataIndex === 'subjectCount'">
            <a-button type="link" @click="linkToMainBody(record)"> {{ text || 0 }} </a-button>
          </template>
        </template>
      </cos-table>
      <editModal ref="editModalRef" @success="success" />
    </dc-layout-content>
  </dc-layout-main>
</template>
<script lang="ts">
import { onMounted, computed, ref } from 'vue'
import { message } from '@dc-platform/dc-components'
import Modal from '@/hooks/modalType'
import dataConfig from '@/views/overseasSales/commonEnum/dataConfig'
import { CustomerCodeParams, CustomerCode } from '@/typings/api/customerCenter'
import { customerCodeGet, customerCodeDelete, customerCodeDetail } from '@/api/overseasSales/customerCenter'
import { getLevelOrGrade } from '@/api/common'
import editModal from './editModal.vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
export default { name: 'DcCosOFbuAccountCode' }
</script>
<script setup lang="ts">
const { t } = useI18n()
const router = useRouter()
const editModalRef = ref()
const tableRef = ref()
const level = ref<
  {
    id: number
    max: number
    min: number
    configName: string
  }[]
>([])
const searchFrom = ref<CustomerCodeParams>({
  stage: [],
  contact: {
    type: 1
  },
  content: ''
})
const columns = computed(() => [
  {
    title: t('arkCode.arkAccount'),
    dataIndex: 'arkAccount',
    cosType: 'link',
    path: '/ofbu/accountCode/view',
    query: ['id', 'arkCode', 'cosId'],
    fixed: 'left',
    width: 150
  },
  {
    title: t('arkCode.arkCode'),
    dataIndex: 'arkCode'
  },
  {
    title: t('arkCode.customerNumber'),
    dataIndex: 'customerNumber'
  },
  {
    title: t('arkCode.customerName'),
    dataIndex: 'customerName'
  },
  {
    title: t('arkCode.cosId'),
    dataIndex: 'cosId',
    width: 120
  },
  {
    title: t('arkCode.arkContactName'),
    dataIndex: 'arkContactName',
    cosType: 'mask',
    width: 120,
    headerQuestion: t('arkCode.arkContactNameDesc')
  },
  {
    title: t('arkCode.arkContactMobile'),
    dataIndex: 'arkContactMobile',
    cosType: 'mask',
    headerQuestion: t('arkCode.arkContactMobileDesc')
  },
  {
    title: t('arkCode.sellerName'),
    dataIndex: 'sellerName',
    headerQuestion: t('arkCode.sellerNameDesc'),
    width: 120
  },
  {
    title: t('arkCode.stage'),
    dataIndex: 'stage',
    dicData: dataConfig.customerPhaseEnum
  },
  {
    title: t('arkCode.level'),
    dataIndex: 'level'
  },
  {
    title: t('arkCode.amount'),
    dataIndex: 'amount',
    width: 120
  },
  {
    title: t('arkCode.arkStatus'),
    dataIndex: 'status',
    dicData: [
      { value: 0, label: t('arkCode.arkCode0') },
      { value: 1, label: t('arkCode.arkCode1') },
      { value: 2, label: t('arkCode.arkCode2') },
      { value: 3, label: t('arkCode.arkCode3') },
      { value: 4, label: t('arkCode.arkCode4') },
      { value: 5, label: t('arkCode.arkCode5') }
    ]
  },
  {
    title: t('arkCode.subjectCount'),
    dataIndex: 'subjectCount'
  },
  {
    title: t('common.createTime'),
    dataIndex: 'createTime',
    width: 190
  },
  {
    title: t('common.action'),
    dataIndex: 'action',
    fixed: 'right',
    width: 120
  }
])
const fetch = (params: CustomerCodeParams, callBack: (res: any) => void) => {
  // 重组档次搜索
  const levelList: { min: number; max: number }[] = []
  if (searchFrom.value.level && searchFrom.value.level.length) {
    searchFrom.value.level.forEach((item) => {
      const obj = level.value.find((v: any) => item === v.id)
      if (obj) {
        levelList.push({
          min: obj.min,
          max: obj.max
        })
      }
    })
  }
  searchFrom.value.levelList = levelList
  customerCodeGet({
    ...params,
    levelList
  }).then((res) => {
    callBack(res)
  })
}
const showMask = (record: CustomerCode, callBack: (res: any) => void) => {
  if (record.id) {
    customerCodeDetail(record.id).then((res) => {
      callBack(res.data)
    })
  }
}
onMounted(() => {
  fetchLevel()
})
const fetchLevel = () => {
  getLevelOrGrade({
    configType: 1,
    appId: 'cos_ofbu'
  }).then((res) => {
    level.value = res.data
  })
}
const addEdit = (row: CustomerCode) => {
  editModalRef.value?.toggle(row)
}
const deleteRow = (row: CustomerCode) => {
  Modal.delete({
    name: row.arkCode,
    onOk() {
      customerCodeDelete({ idList: [row.id] }).then(() => {
        message.success(t('tips.delSuccess'))
        tableRef.value.delFetch()
      })
    }
  })
}
const success = (type = 'edit') => {
  if (type === 'add') {
    tableRef.value?.search()
  } else {
    tableRef.value?.fetch()
  }
}
// 跳转到签约主体列表
const linkToMainBody = (row: CustomerCode) => {
  if (!row.subjectCount) {
    return
  }
  router.push({
    path: '/ofbu/mainBody',
    query: {
      customerCode: row.arkCode
    }
  })
}
</script>
